/**
 * The TMR session endpoints (`/api/v1/auth/session*` and `/api/v1/auth/me`).
 *
 * The session is an httpOnly cookie: the browser never holds a token, and every call here is
 * same-origin with `credentials: 'include'`. Two ways in — the Clerk exchange (`authorization.ts`) and
 * the local break-glass login below — issue the same cookie, so everything after sign-in is identical
 * whichever was used.
 *
 * These deliberately use `fetch` rather than `@tmr/api-client`: a 401 from {@link fetchMe} at boot is
 * the ordinary answer for "nobody is signed in", not a dead session, and routing it through the client
 * would fire its unauthorized handler and bounce the operator off a page they were never on.
 */
import { runtimeConfig } from './runtimeConfig';

/** How the operator authenticated. `local` is the break-glass path, used when Clerk is unavailable. */
export type AuthMethod = 'clerk' | 'local';

/** Who the current session belongs to and how long it is good for (`GET /api/v1/auth/me`, 10 B1). */
export interface TmrSession {
  userId: string;
  email: string;
  name: string;
  authMethod: AuthMethod;
  /** ISO instant the session stops being accepted. What the scheduled refresh is timed against. */
  expiresAt: string;
}

/** Credentials for the break-glass login (05 I3). */
export interface LocalLoginCredentials {
  email: string;
  password: string;
  totpCode: string;
}

/** Thrown when a session call fails. `status` is what the caller branches on, never the message. */
export class SessionError extends Error {
  constructor(
    public readonly status: number,
    message: string,
  ) {
    super(message);
    this.name = 'SessionError';
  }
}

/** True when the failure was "no valid session", which at boot simply means nobody is signed in. */
export function isUnauthenticated(error: unknown): boolean {
  return error instanceof SessionError && error.status === 401;
}

const AUTH_BASE = '/api/v1/auth';

function apiBase(): string {
  return (runtimeConfig('VITE_AUTHZ_API_URL') ?? '').replace(/\/+$/, '');
}

async function sessionRequest(path: string, init: RequestInit = {}): Promise<Response> {
  const headers = new Headers(init.headers);
  headers.set('Accept', 'application/json');
  return fetch(`${apiBase()}${AUTH_BASE}${path}`, { ...init, credentials: 'include', headers });
}

/** Reads the operator's PT-BR `detail` out of a ProblemDetails body, or falls back to a generic line. */
async function toSessionError(response: Response, fallback: string): Promise<SessionError> {
  try {
    const problem = (await response.json()) as { detail?: string };
    const detail = problem.detail?.trim();
    if (detail) return new SessionError(response.status, detail);
  } catch {
    // A failure with no JSON body (proxy error page, network-level 502) still becomes a SessionError.
  }
  return new SessionError(response.status, fallback);
}

/**
 * Who is signed in, straight from the cookie.
 *
 * This is what lets a reloaded page recover the session without asking Clerk — and it is the *only*
 * way the local break-glass login survives a reload at all, since there is no IdP to re-ask (10 B1).
 * A 401 is the normal answer when nobody is signed in; callers check {@link isUnauthenticated}.
 */
export async function fetchMe(): Promise<TmrSession> {
  const response = await sessionRequest('/me');
  if (!response.ok) throw await toSessionError(response, 'Não foi possível ler a sessão.');
  return (await response.json()) as TmrSession;
}

/**
 * Rotates the session, extending it and issuing a new `jti`.
 *
 * Called on a timer derived from `expiresAt` rather than in reaction to a 401: discovering expiry by
 * failing a request means the operator loses whatever they were doing at that moment.
 */
export async function refreshSession(): Promise<TmrSession> {
  const response = await sessionRequest('/session/refresh', { method: 'POST' });
  if (!response.ok) throw await toSessionError(response, 'Não foi possível renovar a sessão.');
  return (await response.json()) as TmrSession;
}

/**
 * Revokes the current session server-side and clears the cookie.
 *
 * Without this, signing out only forgot the session locally: the cookie stayed valid until it expired,
 * so anyone with it could keep using the API (05 §5).
 */
export async function logoutSession(): Promise<void> {
  const response = await sessionRequest('/session/logout', { method: 'POST' });
  // 401 means the session was already gone — which is the state logging out wanted anyway.
  if (!response.ok && response.status !== 401) {
    throw await toSessionError(response, 'Não foi possível encerrar a sessão.');
  }
}

/**
 * Break-glass login for when Clerk is unavailable (05 I3): e-mail + password + TOTP.
 *
 * The backend answers a uniform 401 whichever check failed, on purpose — it must not tell an attacker
 * that the e-mail exists or that the password was right but the code wrong. It also rate-limits by IP,
 * which surfaces as 429; the caller says so rather than repeating "credenciais inválidas".
 */
export async function localLogin(credentials: LocalLoginCredentials): Promise<TmrSession> {
  const response = await sessionRequest('/session/local', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(credentials),
  });

  if (response.status === 429) {
    throw new SessionError(429, 'Muitas tentativas. Aguarde alguns minutos antes de tentar de novo.');
  }
  if (!response.ok) {
    throw await toSessionError(response, 'E-mail, senha ou código inválidos.');
  }
  return (await response.json()) as TmrSession;
}
